import { PageHero } from "@/components/PageHero";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import SmartImage from "@/components/SmartImage";

const SPORTS = ["MMA", "Boxing"];
const LEVELS = ["Pro", "Amateur"];
const PAGE_SIZE = 20;

export default function Records() {
  const [sport, setSport] = useState("MMA");
  const [level, setLevel] = useState("Pro");
  const [weight, setWeight] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filters, setFilters] = useState({ weight_classes: [], cities: [] });
  const [top, setTop] = useState([]);
  const [list, setList] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setWeight(""); setCity(""); }, [sport, level]);

  useEffect(() => {
    api.get("/athletes/filters", { params: { sport, level } }).then((r) => setFilters(r.data)).catch(() => {});
    api.get("/athletes/search", { params: { sport, level, limit: 10 } }).then((r) => setTop(r.data.items)).catch(() => setTop([]));
  }, [sport, level]);

  const filtering = Boolean(weight || city || debouncedQ);

  const load = (page, append) => {
    const id = ++reqId.current; setLoading(true);
    api.get("/athletes/search", { params: { sport, level, weight_class: weight || undefined, city: city || undefined, q: debouncedQ || undefined, page, limit: PAGE_SIZE } })
      .then((r) => { if (id !== reqId.current) return; setList((prev) => ({ ...r.data, items: append ? [...prev.items, ...r.data.items] : r.data.items })); })
      .catch(() => { if (id === reqId.current) setList({ items: [], total: 0, page: 1, pages: 1 }); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
  };
  useEffect(() => { load(1, false); }, [sport, level, weight, city, debouncedQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => { setWeight(""); setCity(""); setQ(""); };

  return (
    <div data-testid="records-page">
      <PageHero eyebrow="Ironveil Athletes" title="The Athlete Records" image="walkout" position="65% center" testId="records-hero">
        Ranked athletes across MMA and Boxing — amateur and professional divisions.
      </PageHero>

      <Section testId="records-body">
        <div className="flex flex-col lg:flex-row gap-4 mb-10 items-stretch">
          <Tabs value={sport} onChange={setSport} options={SPORTS} testid="sport-tabs" />
          <Tabs value={level} onChange={setLevel} options={LEVELS} testid="level-tabs" />
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search athlete, academy, city" className="pl-11 pr-4 py-3 w-full" data-testid="records-search" />
          </div>
        </div>

        {!filtering && top.length > 0 && (
          <div className="mb-16" data-testid="records-top10">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="type-label text-[#C8102E] mb-2">Top {Math.min(top.length, 10)} / {sport} {level}</div>
                <h2 className="type-h2">The Rankings</h2>
              </div>
              <div className="type-meta text-[#666] hidden sm:block">{list.total} athletes in division</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {top.map((a, i) => <TopCard key={a.id} a={a} rank={a.ranking || i + 1} />)}
            </div>
          </div>
        )}

        <div data-testid="records-directory">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
            <div>
              <div className="type-label text-[#C8102E] mb-2">{filtering ? "Search results" : "Full directory"}</div>
              <h2 className="type-h2">{filtering ? `${list.total} Athlete${list.total === 1 ? "" : "s"}` : "All Athletes"}</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <select value={weight} onChange={(e) => setWeight(e.target.value)} className="px-3 py-3 text-xs" data-testid="weight-filter">
                <option value="">All Weight Classes</option>
                {filters.weight_classes.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
              <select value={city} onChange={(e) => setCity(e.target.value)} className="px-3 py-3 text-xs" data-testid="city-filter">
                <option value="">All Cities</option>
                {filters.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {filtering && <button onClick={clear} className="btn-text" data-testid="clear-filters"><X size={14} /> Clear</button>}
            </div>
          </div>

          {list.items.length ? (
            <>
              <div className="card-ive divide-y divide-[#2a2a2a]" data-testid="records-list">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 type-meta text-[#666]">
                  <div className="col-span-1">Rank</div>
                  <div className="col-span-4">Athlete</div>
                  <div className="col-span-2">Weight</div>
                  <div className="col-span-3">Academy</div>
                  <div className="col-span-2 text-right">W · L · D</div>
                </div>
                {list.items.map((a, i) => <Row key={a.id} a={a} rank={a.ranking || i + 1} />)}
              </div>
              <div className="flex items-center justify-between mt-6">
                <div className="type-meta text-[#666]" data-testid="records-count">Showing {list.items.length} of {list.total}</div>
                {list.page < list.pages && (
                  <button onClick={() => load(list.page + 1, true)} disabled={loading} className="btn-secondary" data-testid="load-more">
                    {loading ? "Loading…" : `Load more (${list.total - list.items.length} left)`}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="card-ive p-12 text-center text-[#888]" data-testid="records-empty">
              {loading ? "Loading…" : filtering ? "No athletes match these filters." : `No athletes recorded yet for ${sport} · ${level}.`}
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

function TopCard({ a, rank }) {
  return (
    <Link to={`/athletes/${a.slug}`} className="card-ive relative aspect-[3/4] block group overflow-hidden" data-testid={`top-card-${a.slug}`}>
      <SmartImage path={a.photo_path} fallback="fighter" alt={a.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="absolute top-3 left-3 type-rank text-[#C8102E] leading-none">{String(rank).padStart(2, "0")}</div>
      <div className="absolute inset-x-0 bottom-0 p-4">
        <div className="type-card leading-none">{a.name}</div>
        <div className="type-meta text-[#a0a0a0] mt-2">{a.weight_class}</div>
        <div className="text-sm mt-1"><span className="font-bold">{a.wins}</span><span className="text-[#666]"> · </span>{a.losses}<span className="text-[#666]"> · </span>{a.draws}</div>
      </div>
    </Link>
  );
}

function Row({ a, rank }) {
  return (
    <Link to={`/athletes/${a.slug}`} className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm hover:bg-[#121212] transition-colors group" data-testid={`record-row-${a.slug}`}>
      <div className="col-span-2 md:col-span-1 type-rank text-[#C8102E]">{String(rank).padStart(2, "0")}</div>
      <div className="col-span-10 md:col-span-4 flex items-center gap-3">
        <SmartImage path={a.photo_path} fallback="fighter" alt={a.name} loading="lazy" className="w-11 h-11 object-cover border border-[#2a2a2a]" />
        <div>
          <div className="font-display text-xl sm:text-2xl">{a.name}</div>
          <div className="text-xs text-[#888]">{a.city}</div>
        </div>
      </div>
      <div className="col-span-4 md:col-span-2 text-xs text-[#a0a0a0]">{a.weight_class}</div>
      <div className="col-span-4 md:col-span-3 text-xs text-[#a0a0a0]">{a.academy || "—"}</div>
      <div className="col-span-4 md:col-span-2 text-xs text-right">
        <span className="text-white font-bold text-base">{a.wins}</span>
        <span className="text-[#666]"> · </span>{a.losses}
        <span className="text-[#666]"> · </span>{a.draws}
        <ChevronRight size={14} className="inline ml-2 text-[#666] group-hover:text-[#C8102E] transition-colors" />
      </div>
    </Link>
  );
}

function Tabs({ value, onChange, options, testid }) {
  return (
    <div className="flex border border-[#2a2a2a]" data-testid={testid}>
      {options.map((opt) => (
        <button key={opt} onClick={() => onChange(opt)}
          className={`px-5 py-3 type-meta transition-colors ${value === opt ? "bg-[#C8102E] text-white" : "text-white hover:bg-[#161616]"}`}
          data-testid={`${testid}-${opt.toLowerCase()}`}>{opt}</button>
      ))}
    </div>
  );
}
